import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import {
  type CampaignFacts,
  type CampaignRow,
  campaignRows,
  type OwnSetting,
} from "../src/campaign-list.ts";
import {
  DraftingLevelSchema,
  ScopedDraftingSchema,
  WorkspaceDraftingSchema,
} from "../src/drafting-levels.ts";
import { SignOffModeSchema } from "../src/sign-off-mode.ts";
import { workspaceApi } from "./workspace-context.tsx";

/*
 * What the campaigns list and a campaign's page read, from the three routes that already describe a
 * campaign: its settings and state (`campaign-settings`), its drafting switch (`drafting`) and its
 * sign-off with the mailboxes behind it (`sign-offs`). No new route: each keeps its own contract and
 * its own cache key, so a write on the page invalidates exactly what it changed, as it did when the
 * three lived on three settings sections.
 */

const ResolvedField = <T>(value: z.ZodType<T>) =>
  z.object({ value, source: z.enum(["workspace", "campaign"]) });
export const CalendarModeSchema = z.enum(["booking_link_only", "connector", "manual"]);
const AdmissionPolicySchema = z.enum(["all_replies", "qualified_leads_only"]);
const GoalKind = z.enum([
  "meeting",
  "event_participation",
  "commercial_exploration",
  "contribution_research",
  "resource_delivery",
  "phone_call",
]);
const ParticipantRole = z.enum(["attendee", "speaker", "commercial_partner"]);
const Action = z.enum(["meeting_create", "unsubscribe", "block"]);
const GoalSchema = z.object({
  kind: GoalKind,
  target: z.string(),
  success_criterion: z.string(),
  participant_role: ParticipantRole.nullable(),
});

const MismatchSchema = z.union([
  z.object({
    kind: z.literal("booking_link"),
    found: z.string(),
    expected: z.string().nullable(),
  }),
  z.object({ kind: z.literal("meeting_length"), found: z.number(), expected: z.number() }),
  z.object({ kind: z.literal("event_date"), found: z.string(), expected: z.string() }),
  z.object({ kind: z.literal("registration_link"), found: z.string(), expected: z.string() }),
]);
export type Mismatch = z.infer<typeof MismatchSchema>;

const CampaignSettingsViewSchema = z.object({
  workspaceId: z.string(),
  providerWorkspaceId: z.number().int().positive().optional(),
  /** Whether phone calls run in this workspace; absent from an older server reads as off. */
  phoneCall: z.boolean().optional(),
  settings: z
    .object({
      calendarMode: CalendarModeSchema,
      bookingUrl: z.string().nullable(),
      admissionPolicy: AdmissionPolicySchema,
      allowedActions: z.array(Action),
      signOffMode: SignOffModeSchema.nullable(),
      goal: GoalSchema.nullable(),
      policy: z
        .object({
          permitted_actions: z.array(z.string()),
          owner: z.enum(["agency", "client"]),
          handoff_contact: z
            .object({
              workspace_id: z.number(),
              contact_id: z.string(),
              name: z.string(),
              email: z.string(),
            })
            .nullable()
            .default(null),
        })
        .nullable(),
    })
    .nullable(),
  campaigns: z.array(
    z.object({
      campaignId: z.string(),
      name: z.string(),
      providerCampaignId: z.number(),
      enabled: z.boolean(),
      discovered: z.boolean(),
      firstDraftHold: z
        .object({ reason: z.string(), mismatches: z.array(MismatchSchema) })
        .nullable(),
      noFollowUpAfterNo: z.boolean().nullable(),
      setupState: z
        .enum(["discovered", "needs_setup", "preview", "live", "drifted"])
        .nullable()
        .default(null),
      settings: z.object({
        goal: ResolvedField(GoalSchema.nullable()),
        calendarMode: ResolvedField(CalendarModeSchema),
        bookingUrl: ResolvedField(z.string().nullable()),
        admissionPolicy: ResolvedField(AdmissionPolicySchema),
      }),
      /* A server from before reply activity was added omits it; the list then shows no replies. */
      replies: z
        .object({ lastAt: z.string().nullable(), lastSevenDays: z.number().int().nonnegative() })
        .default({ lastAt: null, lastSevenDays: 0 }),
      /* A server from before the campaign's Bison date was recorded omits it; it then orders last. */
      providerCreatedAt: z.string().nullable().default(null),
    }),
  ),
});
export type CampaignSettingsView = z.infer<typeof CampaignSettingsViewSchema>;
export type CampaignSettingsEntry = CampaignSettingsView["campaigns"][number];
export const campaignSettingsKey = (workspaceId: string) =>
  ["campaign-settings", workspaceId] as const;

const StoredSchema = z
  .object({ state: ScopedDraftingSchema, updatedBy: z.string(), updatedAt: z.string() })
  .nullable();
const DraftingOverviewSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Whether a developer has made drafting available here; a server before LEA-476 has no flag. */
  available: z.boolean().default(true),
  state: WorkspaceDraftingSchema,
  campaigns: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      state: ScopedDraftingSchema,
      stored: StoredSchema.optional(),
      heldBy: DraftingLevelSchema.nullable(),
    }),
  ),
});
type DraftingOverview = z.infer<typeof DraftingOverviewSchema>;
export const draftingKey = (workspaceId: string) => ["drafting", workspaceId] as const;

const MailboxSchema = z.object({
  address: z.string(),
  name: z.string().nullable(),
  detailsSynced: z.boolean(),
  previews: z.object({
    first_name: z.string().nullable(),
    none: z.string().nullable(),
    full_signature: z.string().nullable(),
  }),
  /** Absent from a server before 2026-09-29, which left nothing out. */
  leftOut: z.array(z.string()).default([]),
});
export type Mailbox = z.infer<typeof MailboxSchema>;
const WorkspaceSignOffsSchema = z.object({
  workspaceId: z.string(),
  campaigns: z.array(
    z.object({
      campaignId: z.string(),
      name: z.string(),
      mode: SignOffModeSchema,
      source: z.enum(["workspace", "campaign"]),
      mailboxes: z.array(MailboxSchema),
    }),
  ),
});
export type WorkspaceSignOffs = z.infer<typeof WorkspaceSignOffsSchema>;
export const signOffsKey = (workspaceId: string) => ["sign-offs", workspaceId] as const;

/**
 * How long a campaign's settings, switches and sign-offs are reused across pages (W4): their own
 * writes read them again at once, and another rep's change is picked up within this.
 */
const CAMPAIGN_FRESH_MS = 30_000;

/**
 * One read, with a refusal the rep can act on. A signed-out session and a workspace that is no
 * longer theirs are not "try again" problems, so they say what they are.
 */
async function read<T>(
  url: string,
  schema: z.ZodType<T>,
  failure: string,
  signal?: AbortSignal,
): Promise<T> {
  // A read nobody waits for any more is aborted (W4, F11).
  const response = await fetch(url, { credentials: "same-origin", ...(signal ? { signal } : {}) });
  if (response.status === 401)
    throw new Error("Your session has ended. Sign in again to continue.");
  if (response.status === 404)
    throw new Error("This workspace is not available to you any more. Choose another one.");
  if (!response.ok) throw new Error(failure);
  return schema.parse(await response.json());
}

export function useCampaignSettings(workspaceId: string) {
  return useQuery({
    queryKey: campaignSettingsKey(workspaceId),
    staleTime: CAMPAIGN_FRESH_MS,
    queryFn: ({ signal }) =>
      read(
        `${workspaceApi(workspaceId)}/campaign-settings`,
        CampaignSettingsViewSchema,
        "This workspace's campaigns could not be read.",
        signal,
      ),
  });
}

export function useDraftingOverview(workspaceId: string) {
  return useQuery({
    queryKey: draftingKey(workspaceId),
    staleTime: CAMPAIGN_FRESH_MS,
    queryFn: ({ signal }) =>
      read(
        `${workspaceApi(workspaceId)}/drafting`,
        DraftingOverviewSchema,
        "The drafting switches could not be read.",
        signal,
      ),
  });
}

function useSignOffs(workspaceId: string) {
  return useQuery({
    queryKey: signOffsKey(workspaceId),
    staleTime: CAMPAIGN_FRESH_MS,
    queryFn: ({ signal }) =>
      read(
        `${workspaceApi(workspaceId)}/sign-offs`,
        WorkspaceSignOffsSchema,
        "Sign-off settings could not be read.",
        signal,
      ),
  });
}

/**
 * The settings a campaign sets itself rather than taking from the workspace. Booking is one setting
 * to the rep, although its method and its link are stored apart.
 */
function ownSettings(campaign: CampaignSettingsEntry): OwnSetting[] {
  const own: OwnSetting[] = [];
  if (campaign.settings.goal.source === "campaign") own.push("goal");
  if (
    campaign.settings.calendarMode.source === "campaign" ||
    campaign.settings.bookingUrl.source === "campaign"
  )
    own.push("booking");
  if (campaign.settings.admissionPolicy.source === "campaign") own.push("admission");
  return own;
}

/**
 * One campaign's facts from the three reads. A campaign the drafting read does not list is one it
 * leaves out on purpose, a disabled one, so it has no switch; the same holds for its sign-off.
 */
function campaignFacts(
  view: CampaignSettingsView,
  drafting: DraftingOverview,
  signOffs: WorkspaceSignOffs | null,
): CampaignFacts[] {
  const switches = new Map(drafting.campaigns.map((campaign) => [campaign.id, campaign]));
  const modes = new Map(
    (signOffs?.campaigns ?? []).map((campaign) => [campaign.campaignId, campaign.mode]),
  );
  return view.campaigns.map((campaign) => ({
    campaignId: campaign.campaignId,
    name: campaign.name,
    providerCampaignId: campaign.providerCampaignId,
    enabled: campaign.enabled,
    discovered: campaign.discovered,
    holdReason: campaign.firstDraftHold?.reason ?? null,
    setupState: campaign.setupState,
    goalKind: campaign.settings.goal.value?.kind ?? null,
    calendarMode: campaign.settings.calendarMode.value,
    ownSettings: ownSettings(campaign),
    mismatches: (campaign.firstDraftHold?.mismatches ?? []).map((mismatch) => mismatch.kind),
    /*
     * The drafting read lists every enabled campaign, so one missing from it was found between the
     * two reads. Its switch has no row yet, and a campaign's switch starts off, so it is not
     * drafting: saying "Drafting" about it would be the one wrong claim here.
     */
    draftingHeldBy: switches.has(campaign.campaignId)
      ? (switches.get(campaign.campaignId)?.heldBy ?? null)
      : "campaign",
    signOff: !campaign.enabled
      ? null
      : signOffs === null
        ? "unknown"
        : (modes.get(campaign.campaignId) ?? null),
    replies: campaign.replies,
    providerCreatedAt: campaign.providerCreatedAt,
  }));
}

export type CampaignsRead =
  | { readonly status: "pending" }
  | { readonly status: "error"; readonly message: string; readonly retry: () => void }
  | {
      readonly status: "ready";
      readonly rows: readonly CampaignRow[];
      readonly view: CampaignSettingsView;
      readonly drafting: DraftingOverview;
      /** Null while the sign-off read has failed; everything else still shows. */
      readonly signOffs: WorkspaceSignOffs | null;
      readonly retrySignOffs: () => void;
    };

/**
 * All three reads as one. The list waits for each of them, so a row never shows a state that a
 * column arriving later would contradict. The state needs the settings and the switches, so either
 * failing is the list failing; the sign-off is one column, so its failure costs that column only.
 */
export function useCampaigns(workspaceId: string): CampaignsRead {
  const view = useCampaignSettings(workspaceId);
  const drafting = useDraftingOverview(workspaceId);
  const signOffs = useSignOffs(workspaceId);
  const failed = [view, drafting].find((query) => query.isError);
  if (failed?.error)
    return {
      status: "error",
      message: failed.error.message,
      retry: () => {
        for (const query of [view, drafting]) if (query.isError) void query.refetch();
      },
    };
  if (!view.data || !drafting.data || (!signOffs.data && !signOffs.isError))
    return { status: "pending" };
  const signOffData = signOffs.data ?? null;
  return {
    status: "ready",
    rows: campaignRows(campaignFacts(view.data, drafting.data, signOffData)),
    view: view.data,
    drafting: drafting.data,
    signOffs: signOffData,
    retrySignOffs: () => void signOffs.refetch(),
  };
}
